import React, { useState, useEffect } from 'react';
import { Shield, Filter, Calendar, User, Activity } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { auditService } from '../../services/auditService';
import { AuditLog } from '../../types';

function AuditLogs() {
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: '',
    entityType: '',
    userId: ''
  });

  useEffect(() => {
    loadAuditLogs();
  }, [filters]);

  const loadAuditLogs = async () => {
    try {
      setLoading(true);
      const data = await auditService.getAuditLogs(filters);
      setLogs(data);
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur de chargement',
        message: 'Impossible de charger les logs d\'audit',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const getEntityTypeColor = (entityType: string) => {
    const colors: Record<string, string> = {
      user: 'bg-blue-100 text-blue-800',
      vehicle: 'bg-green-100 text-green-800',
      reservation: 'bg-orange-100 text-orange-800',
      team: 'bg-purple-100 text-purple-800'
    };
    return colors[entityType] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass-card rounded-xl card-hover">
        <div className="px-6 py-6 sm:p-8">
          <div className="flex items-center space-x-4">
            <div className="p-2 bg-gradient-to-br from-red-500 to-red-700 rounded-lg shadow-lg">
              <Shield className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Historique des événements</h1>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="glass-card rounded-xl">
        <div className="px-6 py-5 sm:px-8">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <select
              value={filters.action}
              onChange={(e) => setFilters({ ...filters, action: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white/80"
            >
              <option value="">Toutes les actions</option>
              <option value="CREATE_USER">Création utilisateur</option>
              <option value="UPDATE_USER">Modification utilisateur</option>
              <option value="UPDATE_PROFILE">Mise à jour profil</option>
              <option value="CREATE_TEAM">Création équipe</option>
              <option value="UPDATE_TEAM">Modification équipe</option>
            </select>

            <select
              value={filters.entityType}
              onChange={(e) => setFilters({ ...filters, entityType: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white/80"
            >
              <option value="">Tous les types</option>
              <option value="user">Utilisateur</option>
              <option value="team">Équipe</option>
            </select>

            <button
              onClick={() => setFilters({ action: '', entityType: '', userId: '' })}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white/80 hover:bg-white transition-colors"
            >
              Réinitialiser
            </button>
          </div>
        </div>
      </div>

      {/* Logs List */}
      <div className="glass-card rounded-xl">
        <div className="px-6 py-5 sm:px-8 border-b border-gray-200/50">
          <h3 className="text-lg font-medium text-gray-900">
            Événements récents ({logs.length})
          </h3>
        </div>

        {logs.length === 0 ? (
          <div className="text-center py-12">
            <Activity className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun événement</h3>
            <p className="mt-1 text-sm text-gray-500">
              Aucun événement ne correspond aux filtres sélectionnés
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200/50">
            {logs.map((log) => (
              <div key={log.id} className="p-6 hover:bg-gray-50/50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center">
                        <User className="h-5 w-5 text-gray-600" />
                      </div>
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-2">
                        <h4 className="text-sm font-medium text-gray-900">
                          {auditService.getActionLabel(log.action)}
                        </h4>
                        
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          getEntityTypeColor(log.entityType)
                        }`}>
                          {log.entityType}
                        </span>
                      </div>
                      
                      <p className="text-sm text-gray-700 mb-2">
                        {log.description}
                      </p>
                      
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <div className="flex items-center">
                          <User className="h-3 w-3 mr-1" />
                          {auditService.getRoleLabel(log.userRole)}
                        </div>
                        
                        <div className="flex items-center">
                          <Calendar className="h-3 w-3 mr-1" />
                          {new Date(log.timestamp).toLocaleString('fr-FR')}
                        </div>
                        
                        {log.ipAddress && (
                          <div>
                            IP: {log.ipAddress}
                          </div>
                        )}
                      </div>
                      
                      {log.changes && Object.keys(log.changes).length > 0 && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          <div className="text-xs font-medium text-gray-700 mb-1">Modifications :</div>
                          <div className="text-xs text-gray-600">
                            {Object.entries(log.changes).map(([key, value]) => (
                              <div key={key}>
                                <span className="font-medium">{key}:</span> {JSON.stringify(value)}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AuditLogs;